import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { ModuleProps } from '../types';
import AppHeader from '../AppHeader';
import { useLanguage } from '../LanguageContext';
import { el as elM, en as enM } from '../i18n/modules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Stakeholder {
  id: string;
  name: string;
  team?: string;
  role: string;
  organization: string;
  contact: string;
  influence: number;
  interest: number;
  engagement: 'Unaware' | 'Resistant' | 'Neutral' | 'Supportive' | 'Leading';
  strategy: string;
}

const engagementColors: Record<string, { bg: string; text: string }> = {
  Unaware: { bg: '#f3f4f6', text: '#6b7280' },
  Resistant: { bg: '#fee2e2', text: '#991b1b' },
  Neutral: { bg: '#fef9c3', text: '#854d0e' },
  Supportive: { bg: '#dbeafe', text: '#1e40af' },
  Leading: { bg: '#dcfce7', text: '#166534' },
};

const MATRIX_SIZE = 360;

const Stakeholders: React.FC<ModuleProps> = ({ project, user, token, onBack, canEdit, onLogout, onProfileEdit}) => {
  const { language } = useLanguage();
  const M = language === 'en' ? enM : elM;
  const L = M.stakeholders;
  const C = M.common;
  const [activeTab, setActiveTab] = useState<'register' | 'matrix'>('register');
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStakeholder, setEditingStakeholder] = useState<Stakeholder | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<Stakeholder>>({
    name: '', team: '', role: '', organization: '', contact: '',
    influence: 3, interest: 3, engagement: 'Neutral', strategy: '',
  });
  const [budgetTeams, setBudgetTeams] = useState<string[]>([]);

  useEffect(() => { fetchData(); fetchBudgetTeams(); }, []);

  const fetchBudgetTeams = async () => {
    try {
      const res = await axios.get(`${API}/modules/${project.id}/Budget`, { headers: { Authorization: `Bearer ${token}` } });
      const teams: { name: string }[] = res.data?.data?.teams || [];
      setBudgetTeams(teams.map(t => t.name).filter(Boolean));
    } catch { }
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `${API}/modules/${project.id}/Stakeholders`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.data?.stakeholders) {
        setStakeholders(response.data.data.stakeholders);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const saveStakeholders = async (updated: Stakeholder[]) => {
    await axios.put(
      `${API}/modules/${project.id}/Stakeholders`,
      { data: { stakeholders: updated }, userId: user.id },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const syncOwnerReferences = async (oldName: string, newName: string) => {
    if (!oldName || oldName === newName) return;
    try {
      const [riskRes, issueRes] = await Promise.all([
        axios.get(`${API}/modules/${project.id}/Risk%20Management`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/modules/${project.id}/Issues`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const riskData = riskRes.data?.data;
      if (riskData?.risks?.length) {
        let changed = false;
        const updatedRisks = riskData.risks.map((r: any) => {
          if (r.owner === oldName) { changed = true; return { ...r, owner: newName }; }
          return r;
        });
        if (changed) {
          await axios.put(`${API}/modules/${project.id}/Risk%20Management`, { data: { ...riskData, risks: updatedRisks }, userId: user.id }, { headers: { Authorization: `Bearer ${token}` } });
        }
      }

      const issueData = issueRes.data?.data;
      if (issueData?.issues?.length) {
        let changed = false;
        const updatedIssues = issueData.issues.map((i: any) => {
          if (i.owner === oldName) { changed = true; return { ...i, owner: newName }; }
          return i;
        });
        if (changed) {
          await axios.put(`${API}/modules/${project.id}/Issues`, { data: { ...issueData, issues: updatedIssues }, userId: user.id }, { headers: { Authorization: `Bearer ${token}` } });
        }
      }

      // Sync Daily Log assignees — newName='' when stakeholder is deleted (→ unassigned)
      await axios.put(
        `${API}/daily-log/${project.id}/bulk-reassign`,
        { oldAssignee: oldName, newAssignee: newName },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (err) { console.error('Sync owner references failed:', err); }
  };

  const syncWithOrgChart = async (updated: Stakeholder[]) => {
    try {
      const orgRes = await axios.get(
        `${API}/modules/${project.id}/Organization%20Chart`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const orgData = orgRes.data?.data;
      if (!orgData || !orgData.members?.length) return;
      let changed = false;
      const updatedMembers = orgData.members.map((member: any) => {
        const matchedStakeholder = updated.find(s => s.name === member.name);
        if (matchedStakeholder) {
          changed = true;
          return { ...member, role: matchedStakeholder.role, organization: matchedStakeholder.organization, contact: matchedStakeholder.contact, team: matchedStakeholder.team || undefined };
        }
        return member;
      });
      if (changed) {
        await axios.put(
          `${API}/modules/${project.id}/Organization%20Chart`,
          { data: { ...orgData, members: updatedMembers }, userId: user.id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (err) { console.error('Sync with org chart failed:', err); }
  };

  const handleSave = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      const newStakeholder: Stakeholder = {
        id: editingStakeholder?.id || `S${Date.now()}`,
        name: form.name || '', team: form.team || '', role: form.role || '',
        organization: form.organization || '', contact: form.contact || '',
        influence: form.influence ?? 3, interest: form.interest ?? 3,
        engagement: form.engagement || 'Neutral', strategy: form.strategy || '',
      };
      const updated = editingStakeholder
        ? stakeholders.map(s => s.id === editingStakeholder.id ? newStakeholder : s)
        : [...stakeholders, newStakeholder];
      await saveStakeholders(updated);
      setStakeholders(updated);
      if (editingStakeholder) {
        await syncWithOrgChart(updated);
        await syncOwnerReferences(editingStakeholder.name, form.name || '');
      }
      resetForm();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    const deleted = stakeholders.find(s => s.id === id);
    const updated = stakeholders.filter(s => s.id !== id);
    await saveStakeholders(updated);
    setStakeholders(updated);
    if (deleted?.name) await syncOwnerReferences(deleted.name, '');
  };

  const resetForm = () => {
    setForm({ name: '', team: '', role: '', organization: '', contact: '', influence: 3, interest: 3, engagement: 'Neutral', strategy: '' });
    setEditingStakeholder(null);
    setShowModal(false);
  };

  const getQuadrant = (influence: number, interest: number) => {
    const hi = influence >= 3;
    const hInt = interest >= 3;
    if (hi && hInt) return { label: 'Manage Closely', color: '#ef4444', bg: '#fee2e2' };
    if (hi && !hInt) return { label: 'Keep Satisfied', color: '#f59e0b', bg: '#fef9c3' };
    if (!hi && hInt) return { label: 'Keep Informed', color: '#0ea5e9', bg: '#dbeafe' };
    return { label: 'Monitor', color: '#6b7280', bg: '#f3f4f6' };
  };

  const getMatrixPos = (s: Stakeholder) => {
    const padding = 24;
    const usableSize = MATRIX_SIZE - padding * 2;
    return { x: padding + (s.interest / 5) * usableSize, y: padding + (1 - s.influence / 5) * usableSize };
  };

  const exportToExcel = () => {
    const [h0, h1, h2, h3, h4, h5, h6, h7, h8] = L.excelHeaders;
    const data = stakeholders.map((s, i) => ({
      '#': i + 1, [h0]: s.name, [h1]: s.role, [h2]: s.organization,
      [h3]: s.contact, [h4]: s.influence, [h5]: s.interest,
      [h6]: s.engagement, [h7]: getQuadrant(s.influence, s.interest).label, [h8]: s.strategy,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    ws['!cols'] = [{ wch: 4 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 25 }, { wch: 14 }, { wch: 16 }, { wch: 12 }, { wch: 18 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Stakeholders');
    XLSX.writeFile(wb, `Stakeholders_${project.name}.xlsx`);
  };

  return (
    <div style={styles.container}>
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />

      <div style={styles.breadcrumb}>
        <span style={styles.breadcrumbLink} onClick={onBack}>{C.myProjects}</span>
        <span style={styles.breadcrumbSep}> / </span>
        <span style={styles.breadcrumbLink} onClick={onBack}>{project.name}</span>
        <span style={styles.breadcrumbSep}> / </span>
        <span style={styles.breadcrumbCurrent}>Stakeholders</span>
      </div>

      <div style={styles.main}>
        <div style={styles.titleRow}>
          <h2 style={styles.pageTitle}>Stakeholders</h2>
          <div style={styles.titleButtons}>
            <button style={styles.exportBtn} onClick={exportToExcel}>⬇ Excel</button>
            {canEdit && (
              <button style={styles.addBtn} onClick={() => setShowModal(true)}>{L.btnAdd}</button>
            )}
          </div>
        </div>

        <div style={styles.tabWrapper}>
          <div style={styles.tabContainer}>
            <button style={{ ...styles.tab, ...(activeTab === 'register' ? styles.tabActive : styles.tabInactive) }} onClick={() => setActiveTab('register')}>Stakeholder Register</button>
            <button style={{ ...styles.tab, ...(activeTab === 'matrix' ? styles.tabActive : styles.tabInactive) }} onClick={() => setActiveTab('matrix')}>Stakeholder Matrix</button>
          </div>
        </div>

        {loading ? <p style={styles.loading}>{C.loading}</p> : (
          <>
            {activeTab === 'register' && (
              <div style={styles.section}>
                {stakeholders.length === 0 ? (
                  <div style={styles.emptyState}>
                    <p>{L.emptyState}</p>
                    <p style={styles.emptySubtext}>{L.emptySubtext}</p>
                  </div>
                ) : (
                  <div style={styles.cardGrid}>
                    {stakeholders.map(s => {
                      const q = getQuadrant(s.influence, s.interest);
                      const ec = engagementColors[s.engagement];
                      return (
                        <div key={s.id} style={styles.stakeholderCard}>
                          <div style={{ ...styles.cardHeader, borderTop: `4px solid ${q.color}` }}>
                            <div style={{ ...styles.cardAvatar, backgroundColor: q.color }}>{s.name.charAt(0).toUpperCase()}</div>
                            <div style={styles.cardInfo}>
                              <div style={styles.cardName}>{s.name}</div>
                              {s.team && <div style={{ fontSize: '11px', color: '#4f46e5', fontWeight: '600', marginBottom: '2px' }}>👥 {s.team}</div>}
                              <div style={{ ...styles.cardRole, color: q.color }}>{s.role}</div>
                              {s.organization && <div style={styles.cardOrg}>{s.organization}</div>}
                              {s.contact && <div style={styles.cardContact}>{s.contact}</div>}
                            </div>
                            {canEdit && (
                              <div style={styles.cardActions}>
                                <button style={styles.editBtn} onClick={() => { setEditingStakeholder(s); setForm(s); setShowModal(true); }}>✎</button>
                                <button style={styles.deleteBtn} onClick={() => handleDelete(s.id)}>✕</button>
                              </div>
                            )}
                          </div>
                          <div style={styles.cardMetrics}>
                            <div style={styles.metricItem}>
                              <div style={styles.metricLabel}>{L.cardInfluence}</div>
                              <div style={styles.metricBarBg}><div style={{ ...styles.metricBar, width: `${(s.influence / 5) * 100}%`, backgroundColor: q.color }} /></div>
                              <div style={styles.metricValue}>{s.influence}/5</div>
                            </div>
                            <div style={styles.metricItem}>
                              <div style={styles.metricLabel}>{L.cardInterest}</div>
                              <div style={styles.metricBarBg}><div style={{ ...styles.metricBar, width: `${(s.interest / 5) * 100}%`, backgroundColor: '#0ea5e9' }} /></div>
                              <div style={styles.metricValue}>{s.interest}/5</div>
                            </div>
                          </div>
                          <div style={styles.cardBadges}>
                            <span style={{ ...styles.engagementBadge, backgroundColor: ec.bg, color: ec.text }}>{s.engagement}</span>
                            <span style={{ ...styles.quadrantBadge, backgroundColor: q.bg, color: q.color }}>{q.label}</span>
                          </div>
                          {s.strategy && (
                            <div style={styles.strategy}>
                              <div style={styles.strategyLabel}>{L.cardStrategy}</div>
                              <div style={styles.strategyText}>{s.strategy}</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'matrix' && (
              <div style={styles.section}>
                <div style={styles.matrixLayout}>
                  <div style={styles.matrixLeft}>
                    <div style={styles.matrixTitle}>Power/Interest Grid</div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <div style={styles.yAxisLabelWrapper}>
                        <span style={styles.yAxisLabel}>{L.matrixInfluenceAxis}</span>
                      </div>
                      <div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <div style={styles.yTicks}>
                            {[5, 4, 3, 2, 1, 0].map(n => <span key={n} style={styles.tick}>{n}</span>)}
                          </div>
                          <div style={{ position: 'relative', width: `${MATRIX_SIZE}px`, height: `${MATRIX_SIZE}px`, border: '2px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                            <div style={{ position: 'absolute', top: 0, left: 0, width: '50%', height: '50%', backgroundColor: '#fff7ed' }} />
                            <div style={{ position: 'absolute', top: 0, right: 0, width: '50%', height: '50%', backgroundColor: '#fff1f2' }} />
                            <div style={{ position: 'absolute', bottom: 0, left: 0, width: '50%', height: '50%', backgroundColor: '#f9fafb' }} />
                            <div style={{ position: 'absolute', bottom: 0, right: 0, width: '50%', height: '50%', backgroundColor: '#f0f9ff' }} />
                            <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', backgroundColor: '#d1d5db' }} />
                            <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px', backgroundColor: '#d1d5db' }} />
                            <div style={{ position: 'absolute', top: '8px', left: '8px', fontSize: '10px', fontWeight: '700', color: '#f59e0b', opacity: 0.8 }}>Keep Satisfied</div>
                            <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '10px', fontWeight: '700', color: '#ef4444', opacity: 0.8 }}>Manage Closely</div>
                            <div style={{ position: 'absolute', bottom: '8px', left: '8px', fontSize: '10px', fontWeight: '700', color: '#9ca3af', opacity: 0.8 }}>Monitor</div>
                            <div style={{ position: 'absolute', bottom: '8px', right: '8px', fontSize: '10px', fontWeight: '700', color: '#0ea5e9', opacity: 0.8 }}>Keep Informed</div>
                            {stakeholders.map(s => {
                              const pos = getMatrixPos(s);
                              const q = getQuadrant(s.influence, s.interest);
                              return (
                                <div key={s.id} style={{ position: 'absolute', left: `${pos.x - 18}px`, top: `${pos.y - 18}px`, width: '36px', height: '36px', borderRadius: '50%', backgroundColor: q.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.2)', border: '2px solid white', zIndex: 10 }} title={`${s.name}\n${s.role}\n${L.cardInfluence}: ${s.influence} | ${L.cardInterest}: ${s.interest}\n${q.label}`}>
                                  {s.name.charAt(0)}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div style={{ display: 'flex', marginLeft: '28px' }}>
                          <div style={styles.xTicks}>{[0, 1, 2, 3, 4, 5].map(n => <span key={n} style={styles.tick}>{n}</span>)}</div>
                        </div>
                        <div style={{ textAlign: 'center', fontSize: '11px', color: '#6b7280', marginTop: '4px', marginLeft: '28px', fontWeight: '500' }}>{L.matrixInterestAxis}</div>
                      </div>
                    </div>
                    <div style={styles.quadrantLegend}>
                      {[
                        { label: 'Manage Closely', color: '#ef4444', bg: '#fee2e2', desc: L.quadrantManageClosely },
                        { label: 'Keep Satisfied', color: '#f59e0b', bg: '#fef9c3', desc: L.quadrantKeepSatisfied },
                        { label: 'Keep Informed', color: '#0ea5e9', bg: '#dbeafe', desc: L.quadrantKeepInformed },
                        { label: 'Monitor', color: '#6b7280', bg: '#f3f4f6', desc: L.quadrantMonitor },
                      ].map(q => (
                        <div key={q.label} style={{ ...styles.legendChip, backgroundColor: q.bg }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: q.color, flexShrink: 0 }} />
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: '600', color: q.color }}>{q.label}</div>
                            <div style={{ fontSize: '10px', color: '#6b7280' }}>{q.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={styles.matrixRight}>
                    <div style={styles.matrixTitle}>Stakeholders</div>
                    <div style={styles.stakeholderList}>
                      {stakeholders.sort((a, b) => (b.influence + b.interest) - (a.influence + a.interest)).map(s => {
                        const q = getQuadrant(s.influence, s.interest);
                        const ec = engagementColors[s.engagement];
                        return (
                          <div key={s.id} style={{ ...styles.listCard, borderLeft: `4px solid ${q.color}` }}>
                            <div style={{ ...styles.listAvatar, backgroundColor: q.color }}>{s.name.charAt(0)}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={styles.listName}>{s.name}</div>
                              <div style={styles.listRole}>{s.role}</div>
                              <div style={styles.listOrg}>{s.organization}</div>
                            </div>
                            <div style={styles.listRight}>
                              <span style={{ ...styles.quadrantBadge, backgroundColor: q.bg, color: q.color }}>{q.label}</span>
                              <span style={{ ...styles.engagementBadge, backgroundColor: ec.bg, color: ec.text }}>{s.engagement}</span>
                              <div style={styles.listScores}>
                                <span style={styles.scoreItem}>⬆ {s.influence}</span>
                                <span style={styles.scoreItem}>👁 {s.interest}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && canEdit && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>{editingStakeholder ? L.modalEditTitle : L.modalNewTitle}</h2>
            {editingStakeholder && (
              <div style={{ backgroundColor: '#ede9fe', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#4f46e5' }}>
                ✦ {L.modalSyncNote}
              </div>
            )}
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldName}</label>
              <input style={styles.input} value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={L.fieldNamePlaceholder} />
            </div>
            <div style={styles.twoCol}>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldTeam}</label>
                <select style={styles.input} value={form.team || ''} onChange={e => setForm({ ...form, team: e.target.value })}>
                  <option value="">{L.fieldTeamNone}</option>
                  {budgetTeams.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldRole}</label>
                <input style={styles.input} value={form.role || ''} onChange={e => setForm({ ...form, role: e.target.value })} placeholder={L.fieldRolePlaceholder} />
              </div>
            </div>
            <div style={styles.twoCol}>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldOrganization}</label>
                <input style={styles.input} value={form.organization || ''} onChange={e => setForm({ ...form, organization: e.target.value })} placeholder={L.fieldOrganizationPlaceholder} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Contact</label>
                <input style={styles.input} value={form.contact || ''} onChange={e => setForm({ ...form, contact: e.target.value })} placeholder="email@example.com" />
              </div>
            </div>
            <div style={styles.twoCol}>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldInfluence(form.influence ?? 3).replace(/\*\*(\d+)\*\*/, '$1')}</label>
                <input type="range" min="0" max="5" step="1" value={form.influence ?? 3} onChange={e => setForm({ ...form, influence: parseInt(e.target.value) })} style={styles.slider} />
                <div style={styles.sliderLabels}><span>0</span><span>2.5</span><span>5</span></div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldInterest(form.interest ?? 3).replace(/\*\*(\d+)\*\*/, '$1')}</label>
                <input type="range" min="0" max="5" step="1" value={form.interest ?? 3} onChange={e => setForm({ ...form, interest: parseInt(e.target.value) })} style={styles.slider} />
                <div style={styles.sliderLabels}><span>0</span><span>2.5</span><span>5</span></div>
              </div>
            </div>
            {(() => {
              const q = getQuadrant(form.influence ?? 0, form.interest ?? 0);
              return <div style={{ ...styles.quadrantPreview, backgroundColor: q.bg, color: q.color }}>{L.fieldQuadrant(q.label).replace(/\*\*(.*)\*\*/, '$1')}</div>;
            })()}
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldEngagement}</label>
              <div style={styles.engagementOptions}>
                {(['Unaware', 'Resistant', 'Neutral', 'Supportive', 'Leading'] as const).map(eng => {
                  const ec = engagementColors[eng];
                  return (
                    <button key={eng} style={{ ...styles.engagementBtn, backgroundColor: form.engagement === eng ? ec.bg : '#f3f4f6', color: form.engagement === eng ? ec.text : '#6b7280', border: form.engagement === eng ? `2px solid ${ec.text}` : '2px solid transparent' }} onClick={() => setForm({ ...form, engagement: eng })}>
                      {eng}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldStrategy}</label>
              <textarea style={{ ...styles.input, height: '80px', resize: 'none' }} value={form.strategy || ''} onChange={e => setForm({ ...form, strategy: e.target.value })} placeholder={L.fieldStrategyPlaceholder} />
            </div>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn} onClick={resetForm}>{C.cancel}</button>
              <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>{saving ? C.saving : C.save}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  header: { backgroundColor: '#1a1a2e', padding: '0 32px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { color: 'white', fontSize: '22px', fontWeight: 'bold', margin: 0 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  userName: { color: 'white', fontSize: '14px' },
  userRole: { backgroundColor: '#4f46e5', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
  breadcrumb: { padding: '16px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: '14px', position: 'sticky' as const, top: 64, zIndex: 99 },
  breadcrumbLink: { color: '#4f46e5', cursor: 'pointer', fontWeight: '500' },
  breadcrumbSep: { color: '#9ca3af', margin: '0 8px' },
  breadcrumbCurrent: { color: '#374151', fontWeight: '500' },
  main: { padding: '32px 48px', width: '100%', boxSizing: 'border-box' },
  titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' },
  pageTitle: { fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  titleButtons: { display: 'flex', gap: '12px' },
  exportBtn: { backgroundColor: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  addBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  tabWrapper: { display: 'flex', justifyContent: 'center', marginBottom: '24px' },
  tabContainer: { display: 'inline-flex', backgroundColor: 'white', borderRadius: '999px', padding: '4px', border: '1px solid #e5e7eb', gap: '2px' },
  tab: { padding: '10px 28px', borderRadius: '999px', border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' },
  tabActive: { backgroundColor: '#1a1a2e', color: 'white' },
  tabInactive: { backgroundColor: 'transparent', color: '#6b7280' },
  loading: { color: '#666' },
  section: { backgroundColor: 'white', borderRadius: '12px', padding: '28px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  emptyState: { textAlign: 'center', padding: '40px', color: '#9ca3af' },
  emptySubtext: { fontSize: '13px', marginTop: '8px' },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' },
  stakeholderCard: { backgroundColor: '#fafafa', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' },
  cardHeader: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '16px', backgroundColor: 'white' },
  cardAvatar: { width: '44px', height: '44px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '18px', fontWeight: '700', flexShrink: 0 },
  cardInfo: { flex: 1 },
  cardName: { fontSize: '15px', fontWeight: '600', color: '#1a1a2e', marginBottom: '2px' },
  cardRole: { fontSize: '13px', fontWeight: '500', marginBottom: '2px' },
  cardOrg: { fontSize: '12px', color: '#6b7280' },
  cardContact: { fontSize: '12px', color: '#9ca3af' },
  cardActions: { display: 'flex', gap: '4px' },
  editBtn: { backgroundColor: '#ede9fe', color: '#4f46e5', border: 'none', width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  deleteBtn: { backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' },
  cardMetrics: { padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' },
  metricItem: { display: 'flex', alignItems: 'center', gap: '8px' },
  metricLabel: { fontSize: '11px', color: '#6b7280', width: '80px', flexShrink: 0 },
  metricBarBg: { flex: 1, height: '6px', backgroundColor: '#e5e7eb', borderRadius: '999px', overflow: 'hidden' },
  metricBar: { height: '100%', borderRadius: '999px' },
  metricValue: { fontSize: '11px', color: '#6b7280', width: '28px', textAlign: 'right' },
  cardBadges: { padding: '8px 16px', display: 'flex', gap: '8px', flexWrap: 'wrap' },
  engagementBadge: { padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '600' },
  quadrantBadge: { padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '500' },
  strategy: { padding: '8px 16px 16px', borderTop: '1px solid #f3f4f6' },
  strategyLabel: { fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' },
  strategyText: { fontSize: '12px', color: '#374151', lineHeight: '1.5' },
  matrixLayout: { display: 'flex', gap: '32px', alignItems: 'flex-start' },
  matrixLeft: { flexShrink: 0 },
  matrixRight: { flex: 1, minWidth: 0 },
  matrixTitle: { fontSize: '15px', fontWeight: '600', color: '#1a1a2e', marginBottom: '16px' },
  yAxisLabelWrapper: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20px' },
  yAxisLabel: { fontSize: '11px', color: '#6b7280', fontWeight: '500', writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap' },
  yTicks: { display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: `${MATRIX_SIZE}px`, paddingRight: '4px' },
  xTicks: { display: 'flex', justifyContent: 'space-between', width: `${MATRIX_SIZE}px` },
  tick: { fontSize: '10px', color: '#9ca3af' },
  quadrantLegend: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '16px' },
  legendChip: { display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', borderRadius: '8px' },
  stakeholderList: { display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: `${MATRIX_SIZE + 100}px`, overflowY: 'auto' },
  listCard: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' },
  listAvatar: { width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px', fontWeight: '700', flexShrink: 0 },
  listName: { fontSize: '14px', fontWeight: '600', color: '#1a1a2e' },
  listRole: { fontSize: '12px', color: '#6b7280' },
  listOrg: { fontSize: '11px', color: '#9ca3af' },
  listRight: { display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end', flexShrink: 0 },
  listScores: { display: 'flex', gap: '8px', marginTop: '2px' },
  scoreItem: { fontSize: '11px', color: '#9ca3af' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '32px', width: '560px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1a1a2e', marginBottom: '24px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333', fontSize: '14px' },
  input: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  slider: { width: '100%', marginTop: '8px' },
  sliderLabels: { display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#9ca3af', marginTop: '2px' },
  quadrantPreview: { padding: '10px 16px', borderRadius: '8px', fontSize: '14px', textAlign: 'center', marginBottom: '16px' },
  engagementOptions: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  engagementBtn: { padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  saveBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
};

export default Stakeholders;
